/**
 * Multi-pane workspace — the dockview-react host mounted on the
 * ``/`` route. Replaces the bare ``<Map />`` Stream A PR 2
 * rendered there; Map now lives inside a dockview panel.
 *
 * Behavior:
 *
 * - **Read on mount:** consume ``preferences.panel_layout`` from
 *   the preferences context (which seeds from the bootstrap
 *   response per REQ-AUTH-062). When present + valid, apply via
 *   ``dockview.fromJSON``. When absent / corrupt / referencing
 *   role-restricted panels the current role can't access, fall
 *   back to :data:`DEFAULT_LAYOUT` per REQ-UI-054 / 047.
 * - **Save (explicit):** :func:`saveCurrentLayout` captures the
 *   current ``dockview.toJSON()`` + the panel semantic list, then
 *   ``PUT /api/me/preferences`` via the provider. Per REQ-UI-052
 *   drag / dock / resize / close changes do NOT auto-save — the
 *   operator triggers this explicitly (Profile save).
 * - **Reset (session only):** :func:`resetLayout` snaps the
 *   current session's layout to :data:`DEFAULT_LAYOUT`. Leaves the
 *   persisted ``panel_layout`` untouched per REQ-UI-055.
 * - **Schema-mismatch fallback:** ``dockview.fromJSON`` failure
 *   (e.g., after a dockview-react upgrade that breaks the
 *   serialized shape) recovers by applying :data:`DEFAULT_LAYOUT` +
 *   warning to console per REQ-UI-054. The user's saved layout
 *   document is unchanged; the next explicit save overwrites it.
 * - **Keyboard:** ``Ctrl+Shift+0`` (Cmd+Shift+0 on macOS)
 *   triggers reset to :data:`DEFAULT_LAYOUT` with a confirmation
 *   dialog per REQ-UI-055.
 *
 * Part 2 (architect 2026-06-08): the named-preset machinery was
 * removed. There is no ``applyPreset`` / ``preset_name`` / preset
 * picker — layouts are captured live on save into a Profile, and the
 * single canonical fallback is :data:`DEFAULT_LAYOUT`. The component
 * publishes its action surface via :class:`MultiPaneApiContext` so the
 * topbar's :class:`PreferencesMenu` (rendered outside the route
 * subtree) can call :func:`saveCurrentLayout` / :func:`resetLayout` /
 * :func:`captureLayout` / :func:`applyLayout` / :func:`togglePanel`.
 *
 * Cross-references:
 *   - ADR-024 §6 (explicit save + two-tier opaque dockview_state).
 *   - REQ-UI-047 (role-filtered panels).
 *   - REQ-UI-052 (explicit save; no auto-save).
 *   - REQ-UI-054 (schema-mismatch fallback → DEFAULT_LAYOUT).
 *   - REQ-UI-055 (Reset layout + Ctrl+Shift+0 shortcut).
 *   - REQ-UI-077 (DEFAULT_LAYOUT is the built-in default Profile shape).
 *   - chrome-spec.md 2026-05-26 amendment §"UI preferences menu".
 *   - docs/attic/mvp/streams/C-multi-pane.md.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import {
  DockviewReact,
  type DockviewApi,
  type DockviewReadyEvent,
  type IDockviewPanelProps,
  type SerializedDockview,
} from "dockview-react";
import "dockview-react/dist/styles/dockview.css";
import "@/styles/dockview-theme-meridian.css";
import { useSession } from "@/auth/session-context";
import { useAnnouncer } from "@/chrome/announcer";
import { panelComponents } from "@/multi-pane/panel-registry";
import { PanelHeaderActions } from "@/multi-pane/panel-header-actions";
import { DEFAULT_LAYOUT } from "@/multi-pane/presets";
import { filterLayoutByRole } from "@/multi-pane/layout-role-filter";
import { type MultiPaneApi, useRegisterMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { LAYOUT_RESTORE_FAILED_MESSAGE, migrateSavedLayout } from "@/multi-pane/layout-migration";
import { useNotify } from "@/notifications/use-notify";
import {
  ALL_PANEL_IDS,
  LAYOUT_SCHEMA_VERSION,
  PANEL_TITLES,
  type PanelId,
  getVisiblePanels,
} from "@/multi-pane/registry-types";
import { usePreferences } from "@/preferences/preferences-context";
import type { SetPreferencesResult } from "@/preferences/preferences-context";
import type { ProfileDocument } from "@/auth/session-types";

/** The Profile document's ``layout`` sub-document (:type:`LayoutSettings`). */
type LayoutSlice = NonNullable<ProfileDocument["layout"]>;

/**
 * Best-effort type-guard for the opaque ``preferences.panel_layout``
 * sub-document. The BFF round-trips this without parsing
 * (REQ-AUTH-060 — "Meridian semantic layer is strict-validated;
 * dockview_state is opaque"); the UI imposes its own shape check
 * at the consumer boundary.
 *
 * Applies :func:`migrateSavedLayout` to the parsed state — retired panel ids
 * (e.g. the PR6-removed ``events`` surface) rewritten to their successor,
 * and the ADR-038 Query tab inserted beside Map for layouts predating it —
 * BEFORE the role-accessibility gate + the dockview ``fromJSON`` apply. Both
 * the bootstrap-sourced and the pre-bootstrap-cache layouts flow through
 * here, so a single hook covers every restore path.
 */
function readSavedDockviewState(panelLayout: unknown): SerializedDockview | null {
  if (panelLayout === null || typeof panelLayout !== "object") {
    return null;
  }
  const rec = panelLayout as Record<string, unknown>;
  const opaque = rec.dockview_state;
  if (opaque === undefined || opaque === null || opaque === "") {
    return null;
  }
  try {
    const parsed: SerializedDockview =
      typeof opaque === "string" ? JSON.parse(opaque) : (opaque as SerializedDockview);
    if (!parsed || typeof parsed !== "object" || !parsed.grid) {
      return null;
    }
    // Migrate the saved layout before any consumer sees it: retired panel
    // ids (events→detections, ADR-032 PR6) are rewritten so a stale
    // persisted ``events`` panel doesn't render blank, and the Query tab is
    // inserted beside Map for layouts predating it (ADR-038 D15).
    return migrateSavedLayout(parsed);
  } catch {
    return null;
  }
}

/**
 * Detect whether we're running on macOS for the keyboard-shortcut
 * Cmd-vs-Ctrl branch. ``navigator.platform`` is deprecated but
 * still the most reliable cross-browser signal.
 */
function isMacPlatform(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  return navigator.platform.toUpperCase().includes("MAC");
}

/**
 * Read the pre-bootstrap inline script's cached panel_layout
 * from ``window.__meridianCachedPanelLayout`` (set in
 * :file:`../../index.html`). Returns the raw object; the
 * workspace's ``initialLayout`` useMemo runs the shape
 * validation via :func:`readSavedDockviewState` like it does
 * for the bootstrap-sourced value.
 *
 * ``unknown`` typing — the inline script's output isn't
 * type-checked at the TS layer; we treat it as untrusted input
 * + validate at the boundary.
 */
function readPreBootstrapCache(): unknown {
  if (typeof window === "undefined") {
    return null;
  }
  const cached = (window as { __meridianCachedPanelLayout?: unknown }).__meridianCachedPanelLayout;
  return cached === undefined ? null : cached;
}

/**
 * Capture the current dockview state into the working-state
 * ``layout`` shape (:type:`LayoutSettings`) per ADR-024 §6 + the
 * Profile document (ADR-033 §3.1). The semantic ``panels[]`` list
 * mirrors which panel IDs are present in the dockview state (each
 * tagged ``open: true`` since dockview only persists open panels).
 *
 * ``preset_name`` is **no longer emitted** (part 2, architect
 * 2026-06-08 — the named-preset machinery was removed). The field
 * stays optional on the wire (:type:`LayoutSettings`); the workspace
 * simply omits it, and the canonical fallback is :data:`DEFAULT_LAYOUT`.
 *
 * ``details_panel_sections`` is **gone** from the layout in v3 —
 * it moved to ``entity_details`` (owned by
 * :func:`useSectionVisibility`). Filters are an empty object for
 * MVP — per-panel filter state is a Stream A PR 5 / Stream D
 * follow-up that consumes the synchronized selection bus.
 */
function captureLayoutForSave(dockviewState: SerializedDockview): LayoutSlice {
  const panels = Object.keys(dockviewState.panels ?? {}).map((id) => ({
    id,
    open: true,
    filters: {},
  }));
  return {
    layout_schema_version: LAYOUT_SCHEMA_VERSION,
    panels,
    dockview_state: JSON.stringify(dockviewState),
  };
}

export function MultiPaneWorkspace(): React.ReactElement {
  // REQ-UI-054's prescribed warning toast. Raised at both recovery sites
  // below; ``dedupeKey`` keeps a re-entrant recovery from stacking copies.
  const notify = useNotify();
  const { workingState, setWorkingState } = usePreferences();
  const { hasAnyRole } = useSession();
  const registerApi = useRegisterMultiPaneApi();
  const { announce } = useAnnouncer();

  const dockviewApiRef = useRef<DockviewApi | null>(null);
  // Disposables for the dockview event listeners attached in
  // :func:`onReady`. Cleaned up on workspace unmount via the
  // side-channel ``useEffect`` below.
  const cleanupRef = useRef<Array<{ dispose: () => void }>>([]);
  // When ``true``, the per-panel add / remove listeners
  // **skip** screen-reader announcements — used by
  // :func:`resetLayout` / :func:`applyLayout` so a layout
  // swap announces a single event rather than a per-panel
  // cascade. Defaults to ``false``; the listeners attached
  // in :func:`onReady` only fire for user-driven actions
  // (Panels menu, close X) because they're attached AFTER
  // the initial ``fromJSON`` returns. REQ-UI-059.
  const suppressPanelAnnouncementsRef = useRef(false);
  // Open-panel inventory, synced from dockview's add/remove
  // events in :func:`onReady`. Drives the Panels menu's
  // open/closed indicator dot + the togglePanel add-vs-focus
  // branch (Stream C PR 2).
  const [openPanelIds, setOpenPanelIds] = useState<ReadonlySet<PanelId>>(new Set());
  // Monotonic layout-changed counter (REQ-UI-074 / ADR-024 §6) — bumped on
  // dockview's ``onDidLayoutChange`` (drag / dock / resize / split / close).
  // Published on the API so the "(modified)" indicator can re-read the live
  // layout when the operator rearranges panels without touching the working
  // state. See :attr:`MultiPaneApi.layoutRevision`.
  const [layoutRevision, setLayoutRevision] = useState(0);

  /**
   * Recompute the set of currently-open panel IDs by probing
   * dockview's registry. Called on mount + on every layout
   * change (add / remove / preset apply / reset).
   *
   * Cheap: probes at most :data:`ALL_PANEL_IDS` items per call;
   * the worst-case 9 lookups per event is negligible vs. a re-
   * render cost.
   */
  const syncOpenPanels = useCallback((api: DockviewApi): void => {
    const ids = new Set<PanelId>();
    for (const id of ALL_PANEL_IDS) {
      if (api.getPanel(id) !== undefined) {
        ids.add(id);
      }
    }
    setOpenPanelIds(ids);
  }, []);

  // Recompute the role-filter on session changes (rare). Used to
  // gate the saved layout's panels at restore time per REQ-UI-047.
  const allowedPanels = useMemo<ReadonlySet<PanelId>>(
    () => new Set(getVisiblePanels(hasAnyRole)),
    [hasAnyRole],
  );

  // The built-in default, surgically role-filtered for the current session
  // (REQ-UI-047 / ADR-024 §6) — the universal apply-fallback used by the mount
  // restore, ``resetLayout``, ``applyLayout``, and the dockview-failure
  // recoveries. An operator gets the operator subset (the gated System Status /
  // Audit Log tabs elided + the grid reflowed); a support engineer / admin gets
  // the fuller picture. ``?? DEFAULT_LAYOUT`` is a typesafe non-null floor —
  // unreachable in practice since every role can see map / platforms /
  // detections / alerts / entity-details.
  const roleFilteredDefault = useMemo<SerializedDockview>(
    () => filterLayoutByRole(DEFAULT_LAYOUT, allowedPanels) ?? DEFAULT_LAYOUT,
    [allowedPanels],
  );

  /**
   * Resolve the initial layout once per (preferences x role)
   * change. The memoised value drives ``onReady``'s
   * ``fromJSON`` call; the workspace re-applies if the user's
   * preferences change after mount (e.g., reconcile after the
   * bootstrap fetch).
   *
   * Source-of-truth precedence per REQ-UI-053:
   *
   * 1. ``workingState.layout`` from the bootstrap response
   *    (most-authoritative — the v3 working-state layout slice
   *    populated by the BFF; ADR-033 §3.1).
   * 2. ``window.__meridianCachedPanelLayout`` from the
   *    pre-bootstrap inline script (read from localStorage
   *    before React mounts; load-bearing if SessionProvider's
   *    render-gating relaxes in a future iteration to let the
   *    workspace render before the bootstrap fetch completes).
   * 3. :data:`DEFAULT_LAYOUT`.
   *
   * Both (1) and (2) are validated for shape + role-accessibility
   * before being accepted; mismatches fall back to the next
   * source per REQ-UI-054.
   */
  const initialLayout = useMemo<SerializedDockview>(() => {
    // Surgically elide role-restricted panels from each candidate layout
    // (REQ-UI-047 / ADR-024 §6) rather than rejecting it wholesale: an operator
    // keeps the accessible part of a saved layout, with the gated tabs dropped
    // + the grid reflowed. A source yields ``null`` only when NONE of its
    // panels are accessible — then we try the next source.
    const fromPreferences = readSavedDockviewState(workingState.layout);
    if (fromPreferences !== null) {
      const filtered = filterLayoutByRole(fromPreferences, allowedPanels);
      if (filtered !== null) {
        return filtered;
      }
      console.warn(
        "[multi-pane] saved layout (from working state) has no panels this " +
          "role can access; trying the pre-bootstrap cache, then the default.",
      );
    }
    // Pre-bootstrap cache fallback per REQ-UI-053. The inline
    // script in ``index.html`` stashes ``parsed.panel_layout``
    // on ``window.__meridianCachedPanelLayout`` before React
    // mounts. Cache + bootstrap agree under normal use (every
    // successful PUT writes both); the cache is load-bearing
    // for the future architecture where the workspace renders
    // before the bootstrap fetch returns.
    const fromCache = readSavedDockviewState(readPreBootstrapCache());
    if (fromCache !== null) {
      const filtered = filterLayoutByRole(fromCache, allowedPanels);
      if (filtered !== null) {
        return filtered;
      }
    }
    // The built-in default, role-filtered so the universal fallback is itself
    // always role-correct (the lifted "DEFAULT_LAYOUT is operator-accessible"
    // invariant — ADR-024 §6).
    return roleFilteredDefault;
  }, [workingState.layout, allowedPanels, roleFilteredDefault]);

  /** dockview-react's ``onReady`` — runs once the DockviewApi is
   * available. Applies the resolved initial layout + falls back
   * to :data:`DEFAULT_LAYOUT` on deserialization failure per
   * REQ-UI-054. Also wires the add / remove event listeners that
   * keep :data:`openPanelIds` in sync with the live workspace
   * (Stream C PR 2 — Panels menu's open/closed indicator). */
  const onReady = useCallback(
    (event: DockviewReadyEvent) => {
      dockviewApiRef.current = event.api;
      try {
        event.api.fromJSON(initialLayout);
      } catch (err) {
        console.warn("[multi-pane] dockview.fromJSON failed; applying the default layout", err);
        // REQ-UI-054: tell the OPERATOR, not just the console — their layout
        // just silently reverted and they need to know why (CLAUDE.md §8.1).
        notify({
          severity: "warning",
          title: LAYOUT_RESTORE_FAILED_MESSAGE,
          source: "system",
          dedupeKey: "layout-restore-failed",
        });
        try {
          event.api.fromJSON(roleFilteredDefault);
        } catch (inner) {
          // Defensive — DEFAULT_LAYOUT is hand-authored + tested. If
          // this fails, something is very wrong with dockview's
          // grid resolution and we want it visible in console.
          console.error("[multi-pane] the default layout also failed to apply", inner);
        }
      }
      // Sync once after the initial fromJSON, then keep tracking.
      // Listeners attached AFTER the initial fromJSON returns so
      // the initial panel-load doesn't fire add announcements.
      syncOpenPanels(event.api);
      const onAdd = event.api.onDidAddPanel((panel) => {
        syncOpenPanels(event.api);
        if (suppressPanelAnnouncementsRef.current) {
          return;
        }
        const title = PANEL_TITLES[panel.id as PanelId] ?? panel.id;
        announce(`Panel added: ${title}.`);
      });
      const onRemove = event.api.onDidRemovePanel((panel) => {
        syncOpenPanels(event.api);
        if (suppressPanelAnnouncementsRef.current) {
          return;
        }
        const title = PANEL_TITLES[panel.id as PanelId] ?? panel.id;
        announce(`Panel removed: ${title}.`);
      });
      // Bump the layout-changed counter on ANY layout mutation (drag / dock /
      // resize / split / close), not just add/remove — this is the reactive
      // signal the "(modified)" indicator re-reads ``captureLayout()`` on
      // (REQ-UI-074 / ADR-024 §6). Attached AFTER the initial ``fromJSON`` so
      // the mount restore doesn't register as an operator change.
      const onLayoutChange = event.api.onDidLayoutChange(() => {
        setLayoutRevision((rev) => rev + 1);
      });
      // dockview's onReady returns void; we attach cleanup to the
      // workspace's unmount via a side-channel ``useEffect``
      // below (see ``cleanupRef``). Stash the disposables there.
      cleanupRef.current = [onAdd, onRemove, onLayoutChange];
    },
    [initialLayout, syncOpenPanels, announce, roleFilteredDefault, notify],
  );

  const saveCurrentLayout = useCallback(async (): Promise<SetPreferencesResult> => {
    const api = dockviewApiRef.current;
    if (api === null) {
      return {
        status: "error",
        message: "Layout not ready yet — please try again.",
      };
    }
    const dockviewState = api.toJSON();
    // Patch only the ``layout`` slice of the working state; the
    // provider's ``setWorkingState`` round-trips theme / units /
    // clocks / map / entity_details unchanged (REQ-AUTH-060). The
    // section defaults live in ``entity_details`` now, so the old
    // ``details_panel_sections`` round-trip is no longer needed here.
    return setWorkingState({
      layout: captureLayoutForSave(dockviewState),
    });
  }, [setWorkingState]);

  const resetLayout = useCallback((): void => {
    const api = dockviewApiRef.current;
    if (api === null) {
      return;
    }
    // Suppress the per-panel add/remove cascade so the reset reads
    // as a single user-visible event per REQ-UI-059.
    suppressPanelAnnouncementsRef.current = true;
    try {
      api.clear();
      api.fromJSON(roleFilteredDefault);
    } finally {
      suppressPanelAnnouncementsRef.current = false;
    }
    announce("Layout reset to default.");
  }, [announce, roleFilteredDefault]);

  /**
   * Add or focus a panel per Stream C PR 2's
   * :func:`MultiPaneApi.togglePanel` contract. When the panel
   * is already open, brings its tab to the front. When closed,
   * adds it via ``dockview.addPanel`` at the active group's
   * default position. Returns ``true`` when the panel was
   * added; ``false`` when it was focused (already open).
   *
   * Role-filtering is the menu's responsibility (the menu
   * builds the row list via :func:`getVisiblePanels`); this
   * method trusts the caller.
   */
  const togglePanel = useCallback((id: PanelId): boolean => {
    const api = dockviewApiRef.current;
    if (api === null) {
      return false;
    }
    const existing = api.getPanel(id);
    if (existing !== undefined) {
      existing.api.setActive();
      return false;
    }
    api.addPanel({
      id,
      component: id,
      title: PANEL_TITLES[id],
    });
    return true;
  }, []);

  /**
   * Activate an already-open panel — bring its tab to the front in whatever
   * group hosts it — per :func:`MultiPaneApi.activatePanel`. NEVER opens a
   * closed panel (no ``addPanel``): a ``getPanel(id)`` miss (closed,
   * role-filtered out of this session's workspace, or the API not ready yet)
   * is a no-op returning ``false``. Drives the map→list reveal (REQ-UI-051 —
   * surface the synced list when a map glyph is selected).
   *
   * # A panel already in front is left alone
   *
   * ``setActive`` is not free and not idempotent in the DOM: dockview renders
   * panels through a shared overlay container and **moves** the panel's
   * subtree when a panel is activated. Moving a subtree resets every
   * descendant's ``scrollTop`` — silently, with no ``scroll`` event, no
   * ``scrollTo``, and no visibility change for anything watching.
   *
   * The map→list reveal fires on *every* map-originated selection, so an
   * unconditional ``setActive`` wiped the list's scroll position on each one.
   * When the panel was already fronting that cost the operator the very
   * scroll :func:`useKeepSelectedRowInView` had just performed to bring the
   * selected row into view: measured 2026-10-09 on the hermetic rig, the
   * hook's ``scrollTo`` landed at t=70 ms and dockview moved the subtree at
   * t=71 ms, leaving the selected row off-screen (and permanently so on a
   * quiet list, where no later update re-applies the hold).
   *
   * So an already-visible panel returns ``true`` without touching dockview:
   * the caller asked for the panel to be revealed and it *is* revealed. An
   * api that reports no ``isVisible`` keeps the pre-guard behaviour, so a
   * dockview without the flag never silently stops revealing (CLAUDE.md
   * §8.1 — no new failure mode from a missing fact).
   */
  const activatePanel = useCallback((id: PanelId): boolean => {
    const panel = dockviewApiRef.current?.getPanel(id);
    if (panel === undefined) {
      return false;
    }
    if (panel.api.isVisible === true) {
      return true;
    }
    panel.api.setActive();
    return true;
  }, []);

  /**
   * Capture the live dockview state into a :type:`LayoutSlice`
   * (:type:`LayoutSettings`) per ADR-024 §6 **without** persisting — the
   * read half of the Profile editor's Arrange-layout sub-mode (REQ-UI-077).
   *
   * Identical capture to :func:`saveCurrentLayout` (``toJSON`` →
   * :func:`captureLayoutForSave`) but deliberately stops short of the
   * ``setWorkingState`` PUT: an in-flight arrange edit must not mutate the
   * operator's saved working state — the editor decides when to write (Done
   * → ``draft.layout`` + live preview; never a direct persist here).
   * Returns ``null`` before ``onReady`` has supplied the dockview API.
   */
  const captureLayout = useCallback((): LayoutSlice | null => {
    const api = dockviewApiRef.current;
    if (api === null) {
      return null;
    }
    return captureLayoutForSave(api.toJSON());
  }, []);

  /**
   * Load a :type:`LayoutSlice` (:type:`LayoutSettings`) into the live
   * dockview via the SAME restore path as mount — shape-validate +
   * migrate-retired-panels (:func:`readSavedDockviewState`), role-gate
   * (:func:`layoutIsRoleAccessible`), then ``clear`` + ``fromJSON`` with a
   * :data:`DEFAULT_LAYOUT` corrupt-fallback — **without** persisting
   * (REQ-UI-077 / REQ-UI-054). The write half of the Arrange-layout sub-mode:
   * the editor calls this to restore the pre-arrange workspace on Cancel.
   *
   * The ``dockview_state`` blob is opaque: parsed only for ``fromJSON``,
   * never reshaped (ADR-024 §6). Per-panel add/remove announcements are
   * suppressed so a restore reads as a single event, not a per-panel
   * cascade (REQ-UI-059).
   */
  const applyLayout = useCallback(
    (layout: LayoutSlice): void => {
      const api = dockviewApiRef.current;
      if (api === null) {
        return;
      }
      const parsed = readSavedDockviewState(layout);
      // Surgically elide role-restricted panels (REQ-UI-047 / ADR-024 §6); fall
      // back to the (also role-filtered) default when nothing usable survives
      // — same recovery as the mount path (REQ-UI-054 / 047).
      const filtered = parsed !== null ? filterLayoutByRole(parsed, allowedPanels) : null;
      const next = filtered ?? roleFilteredDefault;
      suppressPanelAnnouncementsRef.current = true;
      try {
        api.clear();
        api.fromJSON(next);
      } catch (err) {
        console.warn("[multi-pane] applyLayout fromJSON failed; applying the default layout", err);
        // Same operator-visible recovery as the mount path (REQ-UI-054).
        notify({
          severity: "warning",
          title: LAYOUT_RESTORE_FAILED_MESSAGE,
          source: "system",
          dedupeKey: "layout-restore-failed",
        });
        try {
          api.clear();
          api.fromJSON(roleFilteredDefault);
        } catch (inner) {
          console.error("[multi-pane] the default layout also failed to apply", inner);
        }
      } finally {
        suppressPanelAnnouncementsRef.current = false;
      }
    },
    [allowedPanels, roleFilteredDefault, notify],
  );

  // Side-channel cleanup for the dockview event listeners
  // attached in :func:`onReady`. dockview-react's onReady has no
  // return-cleanup; we stash the disposables on a ref and
  // dispose them on workspace unmount.
  useEffect(() => {
    return () => {
      for (const d of cleanupRef.current) {
        d.dispose();
      }
      cleanupRef.current = [];
    };
  }, []);

  // Publish the workspace API to the context so the preferences-
  // menu can call into it. Re-registers when the bound handlers'
  // dependencies change — the provider stores the latest reference
  // so the menu always reads the current closure.
  useEffect(() => {
    const api: MultiPaneApi = {
      saveCurrentLayout,
      resetLayout,
      openPanelIds,
      layoutRevision,
      togglePanel,
      activatePanel,
      captureLayout,
      applyLayout,
    };
    registerApi(api);
    return () => {
      registerApi(null);
    };
  }, [
    registerApi,
    saveCurrentLayout,
    resetLayout,
    openPanelIds,
    layoutRevision,
    togglePanel,
    activatePanel,
    captureLayout,
    applyLayout,
  ]);

  // Ctrl+Shift+0 reset shortcut per REQ-UI-055. Confirmation
  // dialog precedes the reset, which re-applies :data:`DEFAULT_LAYOUT`.
  // ``window.confirm`` is the MVP shape — a styled in-popover
  // confirmation lands in a later polish pass if operator feedback
  // warrants it.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const modifier = isMacPlatform() ? event.metaKey : event.ctrlKey;
      if (modifier && event.shiftKey && event.key === "0") {
        event.preventDefault();
        const proceed = window.confirm("Reset layout to default? Your saved layout is unchanged.");
        if (proceed) {
          resetLayout();
        }
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [resetLayout]);

  // Ctrl+Shift+M maximize / restore shortcut per REQ-UI-059.
  // Toggles the active group's maximized state: if any group
  // is currently maximized, restore (any-group is the right
  // condition; the user might have maximized one then focused
  // another, in which case "restore" returns the layout to its
  // non-maximized state). Else maximize the active group.
  //
  // Announces "Panel maximized." / "Panel restored." to the
  // screen-reader live region per REQ-UI-059.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const modifier = isMacPlatform() ? event.metaKey : event.ctrlKey;
      const isM = event.key === "m" || event.key === "M";
      if (!modifier || !event.shiftKey || !isM) {
        return;
      }
      const api = dockviewApiRef.current;
      if (api === null) {
        return;
      }
      event.preventDefault();
      if (api.hasMaximizedGroup()) {
        api.exitMaximizedGroup();
        announce("Panel restored.");
        return;
      }
      const active = api.activeGroup;
      if (active === undefined) {
        // No active group — nothing to maximize. Defensive guard
        // for the cold-mount window before dockview picks an
        // initial active group.
        return;
      }
      const activePanel = active.activePanel;
      if (activePanel === undefined) {
        // Group has no active panel — maximize is a no-op.
        return;
      }
      // dockview v5's ``maximizeGroup`` takes the panel whose
      // group should be maximized (despite the method name); the
      // resulting maximize covers the whole containing group.
      api.maximizeGroup(activePanel);
      announce("Panel maximized.");
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [announce]);

  return (
    <div className="h-full w-full" data-testid="multi-pane-workspace">
      <DockviewReact
        className="dockview-theme-meridian"
        components={panelComponents as Record<string, FunctionComponent<IDockviewPanelProps>>}
        // Per-group header controls (right-actions slot): the map-settings
        // cog (map group only) + the maximize/restore toggle (every group,
        // REQ-UI-059). See :func:`PanelHeaderActions`.
        rightHeaderActionsComponent={PanelHeaderActions}
        onReady={onReady}
      />
    </div>
  );
}
